import { useEffect, useRef, useState } from 'react';
import { useClearRefinements, useRange, type UseRangeProps } from 'react-instantsearch';
import { Button } from '@fpsm/cxy-components/Button';
import { Slider } from '@fpsm/cxy-components/Slider';

import styles from './styles/RangeSliderFilter.module.css';

let timerId: NodeJS.Timeout;
const timeout = 200;

export type RangeSliderFilterProps = UseRangeProps & {
  isDisabled?: boolean;
  label: string;
  showReset?: boolean;
  step?: number;
  withInputs?: boolean;
};

export const RangeSliderFilter = ({
  isDisabled = false,
  label,
  showReset = false,
  step = 1,
  withInputs = false,
  ...props
}: RangeSliderFilterProps) => {
  const { canRefine, range, refine, start } = useRange(props);
  const { canRefine: canRefineClear, refine: clearRefine } = useClearRefinements({
    includedAttributes: [props.attribute],
  });
  const [value, setValue] = useState([range.min, range.max]);
  const hasEverBeenRefinable = useRef(false);

  const from = Number.isFinite(start[0]) ? start[0] : range.min;
  const to = Number.isFinite(start[1]) ? start[1] : range.max;

  const handleValueChange = (values: number[]) => {
    if (value[0] === undefined || value[1] === undefined) return;

    setValue(values);

    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(() => refine([values[0], values[1]]), timeout);
  };

  useEffect(() => {
    setValue([from, to]);
  }, [from, to]);

  const shouldRender = hasEverBeenRefinable.current && typeof value[0] === 'number' && typeof value[1] === 'number';

  if (canRefine && !hasEverBeenRefinable.current) {
    hasEverBeenRefinable.current = true;
  }

  if (!shouldRender) return null;

  return (
    <div className={styles.container}>
      <Slider
        className={styles.rangeSliderFilter}
        disabled={isDisabled || !canRefine}
        label={label}
        max={range.max}
        min={range.min}
        onValueChange={handleValueChange}
        size="s"
        step={step}
        value={value as number[]}
        withInputs={withInputs}
      />
      {showReset && (
        <Button
          className={styles.resetButton}
          disabled={isDisabled || !canRefineClear}
          onClick={clearRefine}
          variant="ghost"
        >
          Reset
        </Button>
      )}
    </div>
  );
};
